import { api } from "../../config/api";
import { appApi } from "./appApi";

export interface GroupsBaseArgs {
  business_id: string | null;
  device_token: string | null;
  session_id: string | null;
}

export interface CaseGroupRow {
  case_group_id?: string | number;
  name: string;
  shortcode: string;
  description: string;
  total_cases?: number;
  created_on?: string;
}

export interface ClientGroupRow {
  client_group_id?: string | number;
  name: string;
  shortcode: string;
  description: string;
  total_clients?: number;
  created_on?: string;
  is_active?: boolean;
}

export interface BusinessTeamMemberRow {
  user_id?: string | number;
  name?: string;
  first_name?: string;
  surname?: string;
  email?: string;
  is_active?: boolean;
}

export interface CaseGroupDetailRow {
  case_group_id?: string | number;
  name: string;
  shortcode: string;
  description: string;
  created_by?: string;
  created_on?: string;
  assigned_owner?: string;
  assigned_owner_id?: string | number;
  total_cases?: number;
}

export interface CaseGroupEntryRow {
  case_id?: string | number;
  case_name?: string;
  client_name?: string;
  service?: string;
  case_status?: string;
  created_on?: string;
  added_by?: string;
  group_case_entry_id?: string | number;
}

export interface CaseListItem {
  case_id?: string | number;
  name?: string;
  case_id_exp?: string;
  service?: string;
  created_date?: string;
  for_assesse_name?: string;
  for_assesse_pan?: string;
  case_curr_state?: string;
  client_name?: string;
  client_pan?: string;
  client_id?: string | number;
  author_name?: string;
  owner?: { owner?: string };
  current_step?: string;
  current_state?: string;
  [key: string]: unknown;
}

export interface GetCasesArgs extends GroupsBaseArgs {
  page_number?: number;
  page_size?: number;
}

export interface GetCaseGroupDetailsArgs extends GroupsBaseArgs {
  case_group_id: string | number;
}

export interface GetCaseGroupEntriesArgs extends GetCaseGroupDetailsArgs {
  page_number: number;
  page_size: number;
}

export interface GetGroupCaseEntryArgs extends GroupsBaseArgs {
  case_id: string | number;
}

export interface CaseGroupDetailItem {
  id: number;
  name: string;
}

export interface CaseGroupDataRow {
  case_id?: string | number;
  case_name?: string;
  group_details?: CaseGroupDetailItem[];
}

export interface ClientGroupClientRow {
  group_id?: string | number;
  client_id?: string | number;
  user_id?: string | number | null;
  username?: string | null;
  full_name?: string;
  email?: string;
  group_name?: string;
  group_shortcode?: string;
  is_active?: boolean;
  client_type?: string;
  pan_id?: string;
  country_code?: string;
  phone?: string;
  status?: string;
  added_by?: string;
}

export interface ClientListItem {
  client_id?: string | number;
  full_name?: string;
  first_name?: string;
  middle_name?: string;
  surname?: string;
  email?: string;
  client_type?: string;
  pan_id?: string;
  country_code?: string;
  phone?: string;
  status?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

export interface GetClientGroupClientsArgs extends GroupsBaseArgs {
  client_group_id: string | number;
}

export interface GetClientsArgs extends GroupsBaseArgs {
  page_number?: number;
  page_size?: number;
}

function parseCaseGroupsList(data: Record<string, unknown>): CaseGroupRow[] {
  const dataObj = data?.data as Record<string, unknown> | undefined;
  const list = (dataObj?.case_group_info ??
    dataObj?.case_groups ??
    data?.case_group_info ??
    data?.case_groups) as unknown[] | undefined;
  if (!Array.isArray(list)) return [];
  return list.map((item: Record<string, unknown>) => ({
    case_group_id: (item.case_group_id ?? item.id) as string | number | undefined,
    name: String(item.name ?? ""),
    shortcode: String(item.shortcode ?? ""),
    description: String(item.description ?? ""),
    total_cases:
      typeof item.case_count === "number"
        ? item.case_count
        : typeof item.total_cases === "number"
          ? item.total_cases
          : 0,
    created_on:
      item.created_date !== undefined && item.created_date !== null
        ? String(item.created_date)
        : item.created_on !== undefined && item.created_on !== null
          ? String(item.created_on)
          : undefined,
  }));
}

function parseBusinessTeamMemberIsActive(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (t === "true" || t === "1") return true;
  }
  return false;
}

function parseBusinessTeam(data: Record<string, unknown>): BusinessTeamMemberRow[] {
  const dataObj = data?.data as Record<string, unknown> | undefined;
  const team = (dataObj?.business_team ?? data?.business_team) as unknown[] | undefined;
  if (!Array.isArray(team)) return [];
  return team.map((u: Record<string, unknown>) => ({
    user_id: u.user_id as string | number | undefined,
    name: u.name as string | undefined,
    first_name: u.first_name as string | undefined,
    surname: u.surname as string | undefined,
    email: u.email as string | undefined,
    is_active: parseBusinessTeamMemberIsActive(u.is_active),
  }));
}

export function filterActiveBusinessTeamForAssignment(
  team: BusinessTeamMemberRow[]
): BusinessTeamMemberRow[] {
  return team.filter((m) => m.is_active === true);
}

function normalizeBusinessId(businessId: string | number | null | undefined): number {
  if (typeof businessId === "number" && !Number.isNaN(businessId)) return businessId;
  if (typeof businessId === "string" && businessId.trim() !== "") {
    const n = Number(businessId);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function coerceIdForApi(id: string | number): string | number {
  if (typeof id === "number" && !Number.isNaN(id)) return id;
  const n = Number(String(id).trim());
  return Number.isNaN(n) ? id : n;
}

function clientGroupIdentifiersForApi(
  groupId: string | number,
  clientGroupId?: string | number | null
): { client_group_id: string | number; group_id: string | number } {
  const gid =
    clientGroupId !== undefined && clientGroupId !== null && String(clientGroupId).trim() !== ""
      ? clientGroupId
      : groupId;
  const groupIdNumeric =
    typeof gid === "number"
      ? gid
      : typeof gid === "string" && /^\d+$/.test(gid.trim())
        ? Number(gid.trim())
        : null;
  return {
    client_group_id: gid,
    group_id: groupIdNumeric !== null && !Number.isNaN(groupIdNumeric) ? groupIdNumeric : gid,
  };
}

export function normalizeClientGroupMemberIdKey(id: string | number | null | undefined): string {
  if (id === null || id === undefined) return "";
  if (typeof id !== "string" && typeof id !== "number") return "";
  const s = String(id).trim();
  if (s === "") return "";
  const n = Number(s);
  if (!Number.isNaN(n)) return String(n);
  return s;
}

function parseClientGroupsList(data: Record<string, unknown>): ClientGroupRow[] {
  const dataObj = data?.data as Record<string, unknown> | undefined;
  const list = (dataObj?.client_groups ?? data?.client_groups) as unknown[] | undefined;
  if (!Array.isArray(list)) return [];
  return list.map((item: Record<string, unknown>) => ({
    client_group_id: (item.client_group_id ?? item.id) as string | number | undefined,
    name: String(item.name ?? ""),
    shortcode: String(item.shortcode ?? ""),
    description: String(item.description ?? ""),
    total_clients: typeof item.total_clients === "number" ? item.total_clients : 0,
    created_on:
      item.created_on !== null && item.created_on !== undefined
        ? String(item.created_on)
        : item.created_date !== null && item.created_date !== undefined
          ? String(item.created_date)
          : item.created_at !== null && item.created_at !== undefined
            ? String(item.created_at)
            : item.date_created !== null && item.date_created !== undefined
              ? String(item.date_created)
              : item.group_created_on !== null && item.group_created_on !== undefined
                ? String(item.group_created_on)
                : item.created !== null && item.created !== undefined
                  ? String(item.created)
                  : undefined,
    is_active: item.is_active !== false,
  }));
}

function parseCaseGroupDetail(data: Record<string, unknown>): CaseGroupDetailRow | null {
  const dataObj = data?.data as Record<string, unknown> | undefined;
  const outer = (dataObj ?? data) as Record<string, unknown>;
  const raw = (outer?.case_group_info ?? outer) as Record<string, unknown>;
  const id = raw.case_group_id ?? raw.id;
  if (id === null || id === undefined) return null;
  const assignedOwner =
    raw.assigned_owner ?? raw.assigned_owner_name ?? raw.assigned_to ?? raw.owner_name;
  const assignedOwnerId = raw.assigned_owner_id ?? raw.assigned_to_id ?? raw.owner_id;
  return {
    case_group_id: id as string | number,
    name: String(raw.name ?? ""),
    shortcode: String(raw.shortcode ?? ""),
    description: String(raw.description ?? ""),
    created_by:
      raw.created_by !== null && raw.created_by !== undefined ? String(raw.created_by) : undefined,
    created_on:
      raw.created_date !== null && raw.created_date !== undefined
        ? String(raw.created_date)
        : raw.created_on !== null && raw.created_on !== undefined
          ? String(raw.created_on)
          : raw.created !== null && raw.created !== undefined
            ? String(raw.created)
            : undefined,
    assigned_owner:
      assignedOwner !== null && assignedOwner !== undefined ? String(assignedOwner) : undefined,
    assigned_owner_id:
      assignedOwnerId !== null && assignedOwnerId !== undefined
        ? (assignedOwnerId as string | number)
        : undefined,
    total_cases:
      typeof raw.case_count === "number"
        ? raw.case_count
        : typeof raw.total_cases === "number"
          ? raw.total_cases
          : undefined,
  };
}

function buildFullName(item: Record<string, unknown>): string | undefined {
  const first =
    item.first_name !== null && item.first_name !== undefined && item.first_name !== ""
      ? String(item.first_name).trim()
      : "";
  const middle =
    item.middle_name !== null && item.middle_name !== undefined && item.middle_name !== ""
      ? String(item.middle_name).trim()
      : "";
  const surname =
    item.surname !== null && item.surname !== undefined && item.surname !== ""
      ? String(item.surname).trim()
      : "";
  const parts = [first, middle, surname].filter(Boolean);
  if (parts.length === 0) {
    if (item.full_name !== null && item.full_name !== undefined && item.full_name !== "")
      return String(item.full_name);
    if (item.name !== null && item.name !== undefined && item.name !== "") return String(item.name);
    return undefined;
  }
  return parts.join(" ");
}

function isClientIdCandidate(c: unknown): c is string | number {
  if (c === null || c === undefined) return false;
  if (typeof c === "string") return c.trim() !== "";
  if (typeof c === "number") return !Number.isNaN(c);
  return false;
}

export function pickGroupMemberClientId(
  item: Record<string, unknown>
): string | number | undefined {
  const candidates = [
    item.client_id,
    item.client_info_id,
    item.assesse_id,
    item.assessee_id,
    item.member_client_id,
    item.user_client_id,
  ];
  for (const c of candidates) {
    if (isClientIdCandidate(c)) return c;
  }
  if (isClientIdCandidate(item.id)) {
    return item.id as string | number;
  }
  return undefined;
}

function parseClientGroupClients(data: Record<string, unknown>): ClientGroupClientRow[] {
  const dataObj = data?.data as Record<string, unknown> | undefined;
  const list = (dataObj?.group_clients ??
    dataObj?.clients ??
    data?.group_clients ??
    data?.clients) as unknown[] | undefined;
  if (!Array.isArray(list)) return [];
  return list.map((item: Record<string, unknown>) => {
    const fullName =
      item.full_name !== null && item.full_name !== undefined && item.full_name !== ""
        ? String(item.full_name)
        : buildFullName(item);
    return {
      group_id: (item.group_id ?? item.client_group_id) as string | number | undefined,
      client_id: pickGroupMemberClientId(item),
      user_id: item.user_id as string | number | null | undefined,
      username:
        item.username !== null && item.username !== undefined ? String(item.username) : null,
      full_name: fullName,
      email:
        item.email !== null && item.email !== undefined && item.email !== ""
          ? String(item.email)
          : undefined,
      group_name:
        item.group_name !== null && item.group_name !== undefined && item.group_name !== ""
          ? String(item.group_name)
          : undefined,
      group_shortcode:
        item.group_shortcode !== null &&
        item.group_shortcode !== undefined &&
        item.group_shortcode !== ""
          ? String(item.group_shortcode)
          : undefined,
      is_active: item.is_active !== false,
      client_type:
        (item.client_type ?? item.client_type_name ?? item.type) !== null &&
        (item.client_type ?? item.client_type_name ?? item.type) !== undefined &&
        String(item.client_type ?? item.client_type_name ?? item.type).trim() !== ""
          ? String(item.client_type ?? item.client_type_name ?? item.type)
          : undefined,
      pan_id:
        (item.client_pan !== null && item.client_pan !== undefined && item.client_pan !== ""
          ? String(item.client_pan)
          : item.pan_id !== null && item.pan_id !== undefined && item.pan_id !== ""
            ? String(item.pan_id)
            : item.pan !== null && item.pan !== undefined && item.pan !== ""
              ? String(item.pan)
              : undefined) ?? undefined,
      country_code:
        item.country_code !== null && item.country_code !== undefined && item.country_code !== ""
          ? String(item.country_code)
          : undefined,
      phone:
        (item.phone ??
          item.phone_number ??
          item.mobile ??
          item.mobile_phone ??
          item.contact_phone ??
          item.client_phone) !== null &&
        (item.phone ??
          item.phone_number ??
          item.mobile ??
          item.mobile_phone ??
          item.contact_phone ??
          item.client_phone) !== undefined &&
        String(
          item.phone ??
            item.phone_number ??
            item.mobile ??
            item.mobile_phone ??
            item.contact_phone ??
            item.client_phone
        ).trim() !== ""
          ? String(
              item.phone ??
                item.phone_number ??
                item.mobile ??
                item.mobile_phone ??
                item.contact_phone ??
                item.client_phone
            )
          : undefined,
      status:
        (item.client_state !== null && item.client_state !== undefined && item.client_state !== ""
          ? String(item.client_state)
          : (item.status as string | undefined)) ?? undefined,
      added_by:
        (item.added_by ??
          item.added_by_name ??
          item.added_by_user ??
          item.member_added_by ??
          item.created_by ??
          item.created_by_name ??
          item.user_name ??
          item.username) !== null &&
        (item.added_by ??
          item.added_by_name ??
          item.added_by_user ??
          item.member_added_by ??
          item.created_by ??
          item.created_by_name ??
          item.user_name ??
          item.username) !== undefined &&
        String(
          item.added_by ??
            item.added_by_name ??
            item.added_by_user ??
            item.member_added_by ??
            item.created_by ??
            item.created_by_name ??
            item.user_name ??
            item.username
        ).trim() !== ""
          ? String(
              item.added_by ??
                item.added_by_name ??
                item.added_by_user ??
                item.member_added_by ??
                item.created_by ??
                item.created_by_name ??
                item.user_name ??
                item.username
            )
          : undefined,
    };
  });
}

function parseClientList(data: Record<string, unknown>): ClientListItem[] {
  const dataObj = data?.data as Record<string, unknown> | undefined;
  const list = (dataObj?.clients ?? data?.clients ?? dataObj?.client_list ?? data?.client_list) as
    | unknown[]
    | undefined;
  if (!Array.isArray(list)) return [];
  return list.map((item: Record<string, unknown>) => ({
    client_id: pickGroupMemberClientId(item),
    full_name: buildFullName(item),
    first_name:
      item.first_name !== null && item.first_name !== undefined && item.first_name !== ""
        ? String(item.first_name)
        : undefined,
    middle_name:
      item.middle_name !== null && item.middle_name !== undefined && item.middle_name !== ""
        ? String(item.middle_name)
        : undefined,
    surname:
      item.surname !== null && item.surname !== undefined && item.surname !== ""
        ? String(item.surname)
        : undefined,
    email:
      item.email !== null && item.email !== undefined && item.email !== ""
        ? String(item.email)
        : undefined,
    client_type:
      item.client_type !== null && item.client_type !== undefined && item.client_type !== ""
        ? String(item.client_type)
        : undefined,
    pan_id:
      (item.client_pan !== null && item.client_pan !== undefined && item.client_pan !== ""
        ? String(item.client_pan)
        : item.pan_id !== null && item.pan_id !== undefined
          ? String(item.pan_id)
          : item.pan !== null && item.pan !== undefined
            ? String(item.pan)
            : undefined) ?? undefined,
    country_code:
      item.country_code !== null && item.country_code !== undefined && item.country_code !== ""
        ? String(item.country_code)
        : undefined,
    phone:
      item.phone !== null && item.phone !== undefined && item.phone !== ""
        ? String(item.phone)
        : item.phone_number !== null && item.phone_number !== undefined
          ? String(item.phone_number)
          : undefined,
    status:
      (item.client_state !== null && item.client_state !== undefined && item.client_state !== ""
        ? String(item.client_state)
        : item.status !== null && item.status !== undefined
          ? String(item.status)
          : undefined) ?? undefined,
    is_active: item.is_active !== false,
  }));
}

function parseCaseGroupEntries(data: Record<string, unknown>): CaseGroupEntryRow[] {
  const dataObj = (data?.data ?? data) as Record<string, unknown> | undefined;
  const caseEntryDetails = (data?.case_entry_details ?? dataObj?.case_entry_details) as
    | Record<string, unknown>
    | undefined;
  const list = (caseEntryDetails?.case_group_entries ??
    dataObj?.cases ??
    dataObj?.case_entries ??
    dataObj?.group_case_entries ??
    data?.cases ??
    data?.case_entries) as unknown[] | undefined;
  if (!Array.isArray(list)) return [];
  return list.map((item: Record<string, unknown>) => ({
    case_id: (item.case_id ?? item.id) as string | number | undefined,
    case_name:
      item.case_name !== null && item.case_name !== undefined
        ? String(item.case_name)
        : item.name !== null && item.name !== undefined
          ? String(item.name)
          : undefined,
    client_name:
      item.client_name !== null && item.client_name !== undefined
        ? String(item.client_name)
        : undefined,
    service: item.service !== null && item.service !== undefined ? String(item.service) : undefined,
    case_status:
      item.case_curr_state !== null && item.case_curr_state !== undefined
        ? String(item.case_curr_state)
        : item.case_status !== null && item.case_status !== undefined
          ? String(item.case_status)
          : item.status !== null && item.status !== undefined
            ? String(item.status)
            : undefined,
    created_on:
      item.created_on !== null && item.created_on !== undefined
        ? String(item.created_on)
        : item.created_date !== null && item.created_date !== undefined
          ? String(item.created_date)
          : undefined,
    added_by:
      item.added_by !== null && item.added_by !== undefined ? String(item.added_by) : undefined,
    group_case_entry_id: (item.group_case_entry_id ?? item.entry_id ?? item.id) as
      | string
      | number
      | undefined,
  }));
}

export const groupsApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getCaseGroups: builder.query<CaseGroupRow[], GroupsBaseArgs>({
      query: (args) => ({
        url: api.urls.GET_ALL_CASE_GROUP || "/cases/getallcasegroup/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: Record<string, unknown>) =>
        parseCaseGroupsList((response.data ?? response) as Record<string, unknown>),
      providesTags: ["CaseGroups"],
    }),
    getClientGroups: builder.query<ClientGroupRow[], GroupsBaseArgs>({
      query: (args) => ({
        url: api.urls.GET_BUSINESS_CLIENT_GROUPS || "/users/getbusinessclientgroups/",
        method: "POST",
        body: {
          business_id: normalizeBusinessId(args.business_id),
          device_token: args.device_token ?? "",
          session_id: args.session_id ?? "",
        },
      }),
      transformResponse: (response: Record<string, unknown>) =>
        parseClientGroupsList((response.data ?? response) as Record<string, unknown>),
      providesTags: ["ClientGroups"],
    }),
    getBusinessTeam: builder.query<BusinessTeamMemberRow[], GroupsBaseArgs>({
      query: (args) => ({
        url: api.urls.GET_BUSINESS_TEAM || "/users/getbusinessteam/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: Record<string, unknown>) =>
        parseBusinessTeam((response.data ?? response) as Record<string, unknown>),
      providesTags: ["BusinessTeam"],
      keepUnusedDataFor: 300,
    }),
    getCaseGroupDetails: builder.query<CaseGroupDetailRow | null, GetCaseGroupDetailsArgs>({
      query: (args) => {
        const businessId =
          typeof args.business_id === "number"
            ? args.business_id
            : typeof args.business_id === "string" && args.business_id.trim() !== ""
              ? Number(args.business_id)
              : 0;
        const caseGroupId =
          typeof args.case_group_id === "number"
            ? args.case_group_id
            : typeof args.case_group_id === "string"
              ? Number(args.case_group_id)
              : 0;
        const body = {
          business_id: Number.isNaN(businessId) ? 0 : businessId,
          case_group_id: Number.isNaN(caseGroupId) ? 0 : caseGroupId,
          device_token: args.device_token ?? "",
          session_id: args.session_id ?? "",
        };
        return {
          url: api.urls.GET_CASE_GROUP_DETAILS || "/cases/getcasegroup/",
          method: "POST",
          body,
        };
      },
      transformResponse: (response: Record<string, unknown>) =>
        parseCaseGroupDetail((response.data ?? response) as Record<string, unknown>),
      providesTags: (_result, _err, args) => [
        { type: "CaseGroupDetail", id: String(args.case_group_id) },
      ],
    }),
    getCases: builder.query<CaseListItem[], GetCasesArgs>({
      query: (args) => ({
        url: api.urls.GET_CASES_URL || "/cases/getcases/",
        method: "POST",
        body: {
          ...args,
          page_number: args.page_number ?? 1,
          page_size: args.page_size ?? 500,
        },
      }),
      transformResponse: (response: Record<string, unknown>): CaseListItem[] => {
        const data = (response?.data ?? response) as Record<string, unknown>;
        const list = data?.cases as unknown[] | undefined;
        return Array.isArray(list) ? (list as CaseListItem[]) : [];
      },
      providesTags: ["Cases"],
    }),
    getGroupCaseEntry: builder.query<CaseGroupDataRow | null, GetGroupCaseEntryArgs>({
      query: (args) => ({
        url: api.urls.GET_GROUP_CASE_ENTRY || "/cases/getgroupcaseentry/",
        method: "POST",
        body: {
          business_id: args.business_id ?? null,
          device_token: args.device_token ?? "",
          session_id: args.session_id ?? "",
          case_id:
            typeof args.case_id === "number" ? args.case_id : Number(args.case_id) || args.case_id,
        },
      }),
      transformResponse: (response: Record<string, unknown>): CaseGroupDataRow | null => {
        const data = response?.data as Record<string, unknown> | undefined;
        const raw = data?.case_group_data as Record<string, unknown> | undefined;
        if (!raw || typeof raw !== "object") return null;
        const groupDetails = raw.group_details as { id?: number; name?: string }[] | undefined;
        return {
          case_id: raw.case_id as string | number | undefined,
          case_name: raw.case_name as string | undefined,
          group_details: Array.isArray(groupDetails)
            ? groupDetails.map((g) => ({ id: Number(g.id) || 0, name: String(g.name ?? "") }))
            : [],
        };
      },
      providesTags: (_result, _err, args) => [{ type: "GroupCaseEntry", id: String(args.case_id) }],
    }),
    getCaseGroupEntries: builder.query<CaseGroupEntryRow[], GetCaseGroupEntriesArgs>({
      query: (args) => {
        const businessId =
          typeof args.business_id === "number"
            ? args.business_id
            : typeof args.business_id === "string" && args.business_id.trim() !== ""
              ? Number(args.business_id)
              : 0;
        const caseGroupId =
          typeof args.case_group_id === "number"
            ? args.case_group_id
            : typeof args.case_group_id === "string"
              ? Number(args.case_group_id)
              : 0;
        const body = {
          business_id: Number.isNaN(businessId) ? 0 : businessId,
          case_group_id: Number.isNaN(caseGroupId) ? 0 : caseGroupId,
          device_token: args.device_token ?? "",
          session_id: args.session_id ?? "",
          page_number: Number(args.page_number) || 1,
          page_size: Number(args.page_size) || 10,
        };
        return {
          url: api.urls.GET_CASE_GROUP_ENTRY || "/cases/getallcasegroupentry/",
          method: "POST",
          body,
        };
      },
      transformResponse: (response: Record<string, unknown>) =>
        parseCaseGroupEntries((response.data ?? response) as Record<string, unknown>),
      providesTags: (_result, _err, args) => [
        { type: "CaseGroupEntries", id: String(args.case_group_id) },
      ],
    }),
    deleteCaseGroupEntry: builder.mutation<
      { success?: boolean },
      {
        case_group_id: string | number;
        case_group_entry_id: (string | number)[];
      } & GroupsBaseArgs
    >({
      query: (args) => {
        const businessId =
          typeof args.business_id === "number"
            ? args.business_id
            : typeof args.business_id === "string" &&
                args.business_id !== "" &&
                args.business_id !== "null"
              ? Number(args.business_id)
              : 0;
        const entryIds = Array.isArray(args.case_group_entry_id)
          ? args.case_group_entry_id
              .map((id) => {
                if (typeof id === "number" && !Number.isNaN(id)) return id;
                if (typeof id === "string" && id.trim() !== "") {
                  const n = Number(id);
                  return Number.isNaN(n) ? id : n;
                }
                return null;
              })
              .filter((id): id is number | string => id !== null)
          : [];
        const body = {
          business_id: Number.isNaN(businessId) ? 0 : businessId,
          case_group_entry_id: entryIds,
          device_token: args.device_token ?? "",
          session_id: args.session_id ?? "",
        };
        return {
          url: api.urls.DELETE_CASE_GROUP_ENTRY || "/cases/deletecasegroupentry/",
          method: "POST",
          body,
        };
      },
      invalidatesTags: (_result, _err, args) => [
        { type: "CaseGroupDetail", id: String(args.case_group_id) },
        { type: "CaseGroupEntries", id: String(args.case_group_id) },
        "CaseGroups",
      ],
    }),
    createCaseGroupEntry: builder.mutation<
      { success?: boolean; errors?: string },
      { case_group_id: string | number; case_ids: (string | number)[] } & GroupsBaseArgs
    >({
      query: (args) => {
        const businessId =
          typeof args.business_id === "number"
            ? args.business_id
            : typeof args.business_id === "string" &&
                args.business_id !== "" &&
                args.business_id !== "null"
              ? Number(args.business_id)
              : 0;
        const caseGroupId =
          typeof args.case_group_id === "number"
            ? args.case_group_id
            : typeof args.case_group_id === "string"
              ? Number(args.case_group_id)
              : 0;
        const caseIds = Array.isArray(args.case_ids)
          ? args.case_ids
              .map((id) => {
                if (typeof id === "number" && !Number.isNaN(id)) return id;
                if (typeof id === "string" && id.trim() !== "") {
                  const n = Number(id);
                  return Number.isNaN(n) ? id : n;
                }
                return null;
              })
              .filter((id): id is number | string => id !== null)
          : [];
        const body = {
          business_id: Number.isNaN(businessId) ? 0 : businessId,
          case_group_id: Number.isNaN(caseGroupId) ? 0 : caseGroupId,
          case_id: caseIds,
          device_token: args.device_token ?? "",
          session_id: args.session_id ?? "",
          is_active: true,
        };
        return {
          url: api.urls.CREATE_CASE_GROUP_ENTRY || "/cases/createcasegroupentry/",
          method: "POST",
          body,
        };
      },
      invalidatesTags: (_result, _err, args) => [
        { type: "CaseGroupDetail", id: String(args.case_group_id) },
        { type: "CaseGroupEntries", id: String(args.case_group_id) },
        "CaseGroups",
      ],
    }),
    createGroupCaseEntry: builder.mutation<
      { success?: boolean; data?: { case_group_data?: unknown[] }; errors?: unknown },
      { case_id: string | number; case_groups: (string | number)[] } & GroupsBaseArgs
    >({
      query: (args) => {
        const caseId =
          typeof args.case_id === "number"
            ? args.case_id
            : typeof args.case_id === "string"
              ? Number(args.case_id)
              : 0;
        const caseGroups = Array.isArray(args.case_groups)
          ? args.case_groups
              .map((id) => {
                if (typeof id === "number" && !Number.isNaN(id)) return id;
                if (typeof id === "string" && id.trim() !== "") {
                  const n = Number(id);
                  return Number.isNaN(n) ? id : n;
                }
                return null;
              })
              .filter((id): id is number | string => id !== null)
          : [];
        return {
          url: api.urls.ASSIGN_CASE_GROUP_ENTRY || "/cases/creategroupcaseentry/",
          method: "POST",
          body: {
            business_id: args.business_id ?? null,
            device_token: args.device_token ?? "",
            session_id: args.session_id ?? "",
            case_id: Number.isNaN(caseId) ? args.case_id : caseId,
            case_groups: caseGroups,
          },
        };
      },
      invalidatesTags: (_result, _err, args) => [
        "CaseGroups",
        "CaseGroupDetail",
        "CaseGroupEntries",
        { type: "GroupCaseEntry", id: String(args.case_id) },
      ],
    }),
    editGroupCaseEntry: builder.mutation<
      { success?: boolean; data?: { case_group_data?: unknown[] }; errors?: unknown },
      { case_id: string | number; case_groups: (string | number)[] } & GroupsBaseArgs
    >({
      query: (args) => {
        const caseId =
          typeof args.case_id === "number"
            ? args.case_id
            : typeof args.case_id === "string"
              ? Number(args.case_id)
              : 0;
        const caseGroups = Array.isArray(args.case_groups)
          ? args.case_groups
              .map((id) => {
                if (typeof id === "number" && !Number.isNaN(id)) return id;
                if (typeof id === "string" && id.trim() !== "") {
                  const n = Number(id);
                  return Number.isNaN(n) ? id : n;
                }
                return null;
              })
              .filter((id): id is number | string => id !== null)
          : [];
        return {
          url: api.urls.UPDATE_ASSIGN_CASE_GROUP_ENTRY || "/cases/editgroupcaseentry/",
          method: "POST",
          body: {
            business_id: args.business_id ?? null,
            device_token: args.device_token ?? "",
            session_id: args.session_id ?? "",
            case_id: Number.isNaN(caseId) ? args.case_id : caseId,
            case_groups: caseGroups,
          },
        };
      },
      invalidatesTags: (_result, _err, args) => [
        "CaseGroups",
        "CaseGroupDetail",
        "CaseGroupEntries",
        { type: "GroupCaseEntry", id: String(args.case_id) },
      ],
    }),
    getClientGroupClients: builder.query<ClientGroupClientRow[], GetClientGroupClientsArgs>({
      query: (args) => {
        const businessId = normalizeBusinessId(args.business_id);
        const gid = args.client_group_id;
        return {
          url: api.urls.GET_GROUP_CLIENTS_NEW || "/users/getgroupclientsnew/",
          method: "POST",
          body: {
            business_id: businessId,
            device_token: args.device_token ?? "",
            session_id: args.session_id ?? "",
            client_group_id: gid,
          },
        };
      },
      transformResponse: (response: Record<string, unknown>) =>
        parseClientGroupClients((response.data ?? response) as Record<string, unknown>),
      providesTags: (_result, _err, args) => [
        { type: "ClientGroupClients", id: String(args.client_group_id) },
      ],
    }),
    getClients: builder.query<ClientListItem[], GetClientsArgs>({
      query: (args) => ({
        url: api.urls.GET_ALL_CLIENTS || "/users/getallclients/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: Record<string, unknown>) =>
        parseClientList((response.data ?? response) as Record<string, unknown>),
    }),
    addClientsToClientGroup: builder.mutation<
      { success?: boolean; count?: number; errors?: string },
      GroupsBaseArgs & {
        group_id: string | number;
        client_group_id?: string | number;
        group_clients: (string | number)[];
      }
    >({
      query: (args) => {
        const businessId = normalizeBusinessId(args.business_id);
        const ids = clientGroupIdentifiersForApi(
          args.group_id,
          args.client_group_id ?? args.group_id
        );
        const groupClients = args.group_clients.map((id) => coerceIdForApi(id));
        return {
          url: api.urls.UPDATE_GROUP_CLIENTS_NEW || "/users/updategroupclientsnew/",
          method: "POST",
          body: {
            business_id: businessId,
            device_token: args.device_token ?? "",
            session_id: args.session_id ?? "",
            ...ids,
            group_clients: groupClients,
          },
        };
      },
      invalidatesTags: (_result, _err, args) => {
        const gid = String(args.client_group_id ?? args.group_id);
        return ["ClientGroups", { type: "ClientGroupClients", id: gid }];
      },
    }),
    removeClientsFromClientGroup: builder.mutation<
      { success?: boolean; errors?: string },
      GroupsBaseArgs & {
        group_id: string | number;
        client_group_id?: string | number;
        group_clients: (string | number)[];
      }
    >({
      query: (args) => {
        const businessId = normalizeBusinessId(args.business_id);
        const ids = clientGroupIdentifiersForApi(
          args.group_id,
          args.client_group_id ?? args.group_id
        );
        const groupClients = args.group_clients.map((id) => coerceIdForApi(id));
        return {
          url: api.urls.UPDATE_GROUP_CLIENTS_NEW || "/users/updategroupclientsnew/",
          method: "POST",
          body: {
            business_id: businessId,
            device_token: args.device_token ?? "",
            session_id: args.session_id ?? "",
            ...ids,
            group_clients: groupClients,
          },
        };
      },
      invalidatesTags: (_result, _err, args) => {
        const gid = String(args.client_group_id ?? args.group_id);
        return ["ClientGroups", { type: "ClientGroupClients", id: gid }];
      },
    }),
    createOrUpdateCaseGroup: builder.mutation<
      { success?: boolean; errors?: string },
      GroupsBaseArgs & {
        name: string;
        shortcode: string;
        description: string;
        case_group_id?: string | number;
        assigned_owner_id?: string | number;
      }
    >({
      query: (args) => {
        const isEdit = args.case_group_id !== null && args.case_group_id !== undefined;
        const businessId =
          typeof args.business_id === "number"
            ? args.business_id
            : typeof args.business_id === "string" && String(args.business_id).trim() !== ""
              ? Number(args.business_id)
              : 0;
        const body: Record<string, unknown> = {
          business_id: Number.isNaN(businessId) ? 0 : businessId,
          device_token: args.device_token ?? "",
          session_id: args.session_id ?? "",
          name: args.name,
          shortcode: args.shortcode,
          description: args.description,
          is_active: true,
        };
        if (isEdit) {
          body.case_group_id = args.case_group_id;
          if (args.assigned_owner_id !== undefined && args.assigned_owner_id !== null) {
            body.assigned_owner_id = args.assigned_owner_id;
          }
        }
        return {
          url: isEdit
            ? api.urls.EDIT_CASE_GROUP || "/cases/editcasegroup/"
            : api.urls.CREATE_CASE_GROUP || "/cases/createcasegroup/",
          method: "POST",
          body,
        };
      },
      invalidatesTags: (_result, _err, args) =>
        args.case_group_id !== null && args.case_group_id !== undefined
          ? ["CaseGroups", { type: "CaseGroupDetail" as const, id: String(args.case_group_id) }]
          : ["CaseGroups"],
    }),
    deleteCaseGroup: builder.mutation<
      { success?: boolean; errors?: string },
      GroupsBaseArgs & { case_group_id: string | number }
    >({
      query: (args) => ({
        url: api.urls.DELETE_CASE_GROUP || "/cases/deletecasegroup/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: ["CaseGroups"],
    }),
    assignCaseGroupOwner: builder.mutation<
      { success?: boolean; errors?: string },
      GroupsBaseArgs & { case_group_id: string | number; assign_to: string | number }
    >({
      query: (args) => ({
        url: api.urls.CASE_GROUP_ASSIGN || "/cases/casegroupassign/",
        method: "POST",
        body: {
          business_id: args.business_id,
          device_token: args.device_token,
          session_id: args.session_id,
          case_group_id: args.case_group_id,
          assign_to: args.assign_to,
        },
      }),
      invalidatesTags: (_result, _err, args) => [
        "CaseGroups",
        { type: "CaseGroupDetail" as const, id: String(args.case_group_id) },
      ],
    }),
    createOrUpdateClientGroup: builder.mutation<
      { success?: boolean; errors?: string },
      GroupsBaseArgs & {
        name: string;
        shortcode: string;
        description: string;
        is_active: boolean;
        client_group_id?: string | number;
      }
    >({
      query: (args) => ({
        url: api.urls.CREATE_CLIENT_GROUP || "/users/createclientgroup/",
        method: "POST",
        body: {
          ...args,
          business_id: normalizeBusinessId(args.business_id),
        },
      }),
      invalidatesTags: ["ClientGroups", "ClientGroupClients"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCaseGroupsQuery,
  useGetClientGroupsQuery,
  useGetBusinessTeamQuery,
  useGetCaseGroupDetailsQuery,
  useGetCaseGroupEntriesQuery,
  useGetGroupCaseEntryQuery,
  useGetCasesQuery,
  useDeleteCaseGroupEntryMutation,
  useCreateCaseGroupEntryMutation,
  useCreateGroupCaseEntryMutation,
  useEditGroupCaseEntryMutation,
  useGetClientGroupClientsQuery,
  useGetClientsQuery,
  useAddClientsToClientGroupMutation,
  useRemoveClientsFromClientGroupMutation,
  useCreateOrUpdateCaseGroupMutation,
  useDeleteCaseGroupMutation,
  useCreateOrUpdateClientGroupMutation,
  useAssignCaseGroupOwnerMutation,
} = groupsApi;
